import type { LevelDefinition } from "@/levels/types";

const STORAGE_KEY = "snake-arena-scoring-v2-best-v1";

export interface ScoreV2State {
  schemaVersion: 1;
  /** Best Final Score (bite + skill + pace) ever reached per level, keyed by the level's stable id. Entirely separate from `ProgressStorage`'s legacy (Bite-Score-only) high score - the two are never compared. */
  bestFinalScoreByLevelId: Record<string, number>;
}

function defaultState(): ScoreV2State {
  return { schemaVersion: 1, bestFinalScoreByLevelId: {} };
}

function isValid(value: unknown): value is ScoreV2State {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<ScoreV2State>;
  return candidate.schemaVersion === 1 && typeof candidate.bestFinalScoreByLevelId === "object" && candidate.bestFinalScoreByLevelId !== null;
}

/**
 * Scoring V2's own local best-score record - a legacy V1 local high score
 * (see `ProgressStorage`) is preserved exactly as-is and never touched by
 * this, and this never claims a "new high score" by comparing a V2 Final
 * Score against a V1 one. Deliberately independent of Phaser - safe to unit
 * test in isolation, and independent of `ProgressStorage` (stars/unlocks/
 * progression stay Bite-Score-derived, this is display-only).
 */
export const ScoreV2Storage = {
  get(): ScoreV2State {
    try {
      const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
      if (!raw) return defaultState();
      const parsed = JSON.parse(raw);
      return isValid(parsed) ? parsed : defaultState();
    } catch {
      return defaultState();
    }
  },

  save(state: ScoreV2State): void {
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage failures - the game still works, just without persistence.
    }
  },

  getBestFinalScore(level: LevelDefinition): number {
    return this.get().bestFinalScoreByLevelId[level.id] ?? 0;
  },

  /**
   * Whether this level has ever actually recorded a Score V2 Final Score -
   * lets a caller (Level Select, the results overlay) distinguish "no
   * record yet" from "a real, recorded 0", so a missing record is never
   * mislabeled as a genuine best of 0.
   */
  hasBestFinalScore(level: LevelDefinition): boolean {
    return level.id in this.get().bestFinalScoreByLevelId;
  },

  /**
   * Records a completed run's Final Score, only ever raising the stored
   * best (never lowering it). A completed Campaign run's Final Score can
   * legitimately be 0 or negative (e.g. CHAOS penalties along the way) -
   * the very first recorded result for a level is always a new best,
   * whatever its value, so this checks presence (via the same
   * `bestFinalScoreByLevelId` membership `hasBestFinalScore()` reads),
   * never a `?? 0` default that would silently reject a genuine first
   * score of exactly 0 or below by comparing it against a phantom "0
   * already on record". Returns whether this run actually set a new V2
   * best.
   */
  recordFinalScore(level: LevelDefinition, finalScore: number): { isNewHighScore: boolean } {
    const state = this.get();
    const hasPrevious = level.id in state.bestFinalScoreByLevelId;
    const previous = state.bestFinalScoreByLevelId[level.id];
    const isNewHighScore = !hasPrevious || finalScore > previous;
    if (isNewHighScore) {
      this.save({ ...state, bestFinalScoreByLevelId: { ...state.bestFinalScoreByLevelId, [level.id]: finalScore } });
    }
    return { isNewHighScore };
  },
};
